import { useAuthenticator } from '@aws-amplify/ui-react';
import { Button, Spinner } from '@nrg/design-system';
import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cognitoErrorAuthKey } from '@/auth/cognito-error-message';
import AuthFooterLink from '@/auth/components/AuthFooterLink';
import AuthScreen from '@/auth/components/AuthScreen';
import CodeField from '@/auth/components/CodeField';

export default function ConfirmSignInScreen() {
  const { t } = useTranslation('auth');
  const { isPending, error, challengeName, submitForm, signOut } = useAuthenticator((c) => [
    c.isPending,
    c.error,
    c.challengeName,
    c.route,
  ]);

  const [code, setCode] = useState('');
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const codeError = submitAttempted && code.length !== 6 ? t('errors.code_six_digits') : undefined;

  const cognitoKey = cognitoErrorAuthKey(error);
  const banner = cognitoKey ? t(cognitoKey) : null;

  const subhead =
    challengeName === 'SMS_MFA'
      ? t('confirm_sign_in.subhead_sms')
      : challengeName === 'EMAIL_OTP'
        ? t('confirm_sign_in.subhead_email')
        : t('confirm_sign_in.subhead_totp');

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitAttempted(true);
    if (code.length !== 6) return;
    submitForm({ confirmation_code: code });
  };

  return (
    <AuthScreen
      heading={t('confirm_sign_in.heading')}
      subhead={subhead}
      error={banner}
      onSubmit={handleSubmit}
      footer={<AuthFooterLink onClick={() => signOut()}>{t('common.sign_out')}</AuthFooterLink>}
    >
      <CodeField
        label={t('confirm_sign_in.subhead_totp')}
        value={code}
        onChange={setCode}
        onComplete={(value) => submitForm({ confirmation_code: value })}
        error={codeError}
        disabled={isPending}
        autoFocus
      />
      <Button
        type="submit"
        variant="primary"
        size="lg"
        fullWidth
        disabled={isPending}
        iconLeft={isPending ? <Spinner size="sm" /> : undefined}
      >
        {t('confirm_sign_in.submit')}
      </Button>
    </AuthScreen>
  );
}
